import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Card, Chip, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { colors, alphaVar } from "@components/ui/colors";

type BillingPlanInfoCardProps = {
  title: string;
  description?: string;
  benefits?: string[];
  price?: {
    amount?: number | string;
    currency?: string;
    frequency?: string;
  };
  billingType?: string;
  isPopular?: boolean;
  isComingSoon?: boolean;
  isCustom?: boolean;
  isFreeTrial?: boolean;
  hexColor?: string;
  expanded?: boolean;
  onToggle?: () => void;
};

const ORANGE = colors.orange.base;

const formatPlanPrice = (price?: BillingPlanInfoCardProps["price"]) => {
  if (!price) return "Custom";

  const amount = Number(price.amount ?? 0);

  if (!amount) return "Free";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: price.currency || "USD",
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
};

const getPlanBillingLabel = ({ billingType, isCustom, isFreeTrial }: Pick<BillingPlanInfoCardProps, "billingType" | "isCustom" | "isFreeTrial">) => {
  if (isFreeTrial) return "Free trial";
  if (isCustom) return "Custom plan";
  if (billingType === "PAY_AS_YOU_GO") return "Pay as you go";

  return "Monthly plan";
};

export const BillingPlanInfoCard = ({
  title,
  description,
  benefits = [],
  price,
  billingType,
  isPopular,
  isComingSoon,
  isCustom,
  isFreeTrial,
  expanded = false,
  onToggle,
}: BillingPlanInfoCardProps) => {
  const billingLabel = getPlanBillingLabel({
    billingType,
    isCustom,
    isFreeTrial,
  });

  const displayBenefits = isComingSoon ? ["Availability coming soon"] : benefits;
  const previewBenefits = displayBenefits.slice(0, 4);
  const hiddenBenefitsCount = Math.max(displayBenefits.length - previewBenefits.length, 0);

  return (
    <Card
      elevation={0}
      sx={{
        height: "auto",
        borderRadius: "18px",
        border: `1px solid ${expanded ? alphaVar(ORANGE, 0.45) : "#none"}`,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        overflow: "hidden",
        transition: "border-color 0.2s ease, background-color 0.2s ease",
      }}
    >
      <Box sx={{ p: 2.25 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                mb: 1,
                flexWrap: "wrap",
                rowGap: 0.75,
              }}
            >
              {isPopular && (
                <Chip
                  label="Popular"
                  size="small"
                  sx={{
                    height: 22,
                    bgcolor: alphaVar(ORANGE, 0.1),
                    color: "#c2410c",
                    fontSize: "0.68rem",
                    fontWeight: 800,
                  }}
                />
              )}

              {isComingSoon && (
                <Chip
                  label="Coming soon"
                  size="small"
                  sx={{
                    height: 22,
                    bgcolor: "var(--fx-surface-muted, #F1F5F9)",
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    fontSize: "0.68rem",
                    fontWeight: 800,
                  }}
                />
              )}

              <Chip
                label={billingLabel}
                size="small"
                sx={{
                  height: 22,
                  bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  fontSize: "0.68rem",
                  fontWeight: 800,
                  border: "1px solid var(--fx-surface-border, #E2E8F0)",
                }}
              />
            </Stack>

            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 900,
                color: "var(--fx-surface-text, #0F172A)",
                lineHeight: 1.25,
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: "0.78rem",
                color: "var(--fx-surface-textsecondary, #64748B)",
                lineHeight: 1.4,
                fontWeight: 500,
              }}
            >
              {description}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={onToggle}
            sx={{
              mt: -0.5,
              bgcolor: alphaVar(ORANGE, 0.08),
              color: ORANGE,
              "&:hover": {
                bgcolor: alphaVar(ORANGE, 0.14),
              },
            }}
          >
            <ExpandMoreRoundedIcon
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          </IconButton>
        </Stack>

        <Box
          sx={{
            mt: 1.75,
            p: 1.5,
            borderRadius: "14px",
            bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: ORANGE }} />

            <Box>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 900,
                  color: "var(--fx-surface-text, #0F172A)",
                  letterSpacing: "-0.4px",
                  lineHeight: 1,
                }}
              >
                {formatPlanPrice(price)}
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: "0.72rem",
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  fontWeight: 700,
                }}
              >
                {billingType === "PAY_AS_YOU_GO"
                  ? `Base price / ${price?.frequency || "month"} + usage`
                  : Number(price?.amount ?? 0) > 0
                    ? `per ${price?.frequency || "month"}`
                    : billingLabel}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ mt: 1.75 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "var(--fx-surface-texttertiary, #94A3B8)",
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Key benefits
            </Typography>

            {hiddenBenefitsCount > 0 && !expanded && (
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: ORANGE,
                  fontWeight: 800,
                }}
              >
                +{hiddenBenefitsCount} more
              </Typography>
            )}
          </Stack>

          <Stack spacing={0.85}>
            {previewBenefits.map((benefit, index) => (
              <Stack key={`${title}-preview-benefit-${index}`} direction="row" spacing={0.8} alignItems="flex-start">
                <Box
                  sx={{
                    mt: "2px",
                    width: 17,
                    height: 17,
                    borderRadius: "999px",
                    bgcolor: alphaVar(ORANGE, 0.1),
                    color: ORANGE,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CheckRoundedIcon sx={{ fontSize: 13 }} />
                </Box>

                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    lineHeight: 1.35,
                    fontWeight: 600,
                    wordBreak: "break-word",
                  }}
                >
                  {benefit}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: "1px solid var(--fx-surface-border, #E2E8F0)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: "var(--fx-surface-textsecondary, #64748B)" }} />

              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  fontWeight: 900,
                }}
              >
                Full benefit list
              </Typography>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                columnGap: 2,
                rowGap: 1,
              }}
            >
              {displayBenefits.map((benefit, index) => (
                <Stack key={`${title}-full-benefit-${index}`} direction="row" spacing={0.8} alignItems="flex-start">
                  <Box
                    sx={{
                      mt: "2px",
                      width: 17,
                      height: 17,
                      borderRadius: "999px",
                      bgcolor: alphaVar(ORANGE, 0.1),
                      color: ORANGE,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CheckRoundedIcon sx={{ fontSize: 13 }} />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "var(--fx-surface-textsecondary, #64748B)",
                      lineHeight: 1.35,
                      fontWeight: 600,
                    }}
                  >
                    {benefit}
                  </Typography>
                </Stack>
              ))}

              {!displayBenefits.length && (
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "var(--fx-surface-texttertiary, #94A3B8)",
                    fontWeight: 600,
                  }}
                >
                  No benefits listed.
                </Typography>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Card>
  );
};
